type Stream = { k: string; d: string };

const categories: Array<{ label: string; title: string; desc: string; streams: Stream[] }> = [
  {
    label: "Revenue Pillar 1",
    title: "Direct Venue Revenue",
    desc: "These revenue channels are generated through daily operation of each ClutchCube location.",
    streams: [
      { k: "Ticket Sales", d: "Per-session admissions with dynamic pricing based on demand, seasonality, and peak usage." },
      { k: "Premium Highlight Videos", d: "Professionally edited cinematic replay packages delivered immediately after the experience." },
      { k: "Birthday & Group Events", d: "Private celebrations, youth sports events, and group bookings." },
      { k: "Corporate Events", d: "Team-building, client entertainment, executive retreats, and hospitality experiences." },
    ],
  },
  {
    label: "Revenue Pillar 2",
    title: "Brand & Community Revenue",
    desc: "These recurring revenue opportunities strengthen customer loyalty while creating additional value for strategic partners.",
    streams: [
      { k: "Merchandise", d: "Branded apparel, personalized memorabilia, and custom highlight products." },
      { k: "Sponsorships", d: "Premium in-venue branding, sponsored challenges, and integrated marketing partnerships." },
      { k: "Team & Venue Partnerships", d: "Official team-themed experiences, venue collaborations, and future licensed content opportunities." },
      { k: "Memberships & Competitive Leagues", d: "Season passes, recurring memberships, tournaments, leaderboards, rankings, and community engagement." },
    ],
  },
  {
    label: "Revenue Pillar 3",
    title: "Platform & Franchise Revenue",
    desc: "Once the flagship location validates the operating model, these channels support national and international expansion.",
    streams: [
      { k: "Franchise Fees", d: "Initial franchise licensing and ongoing franchise development." },
      { k: "Software & Content Licensing", d: "Recurring software platform access, operational tools, analytics, and content management." },
      { k: "Licensed Moment Packs", d: "Future premium content packages featuring officially licensed teams, leagues, venues, and special event experiences, subject to applicable licensing agreements." },
    ],
  },
];

export function Business() {
  let counter = 0;
  return (
    <section id="business" className="relative py-32 bg-arena">
      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-3xl mb-16">
          <p className="text-xs uppercase tracking-[0.3em] text-primary mb-4">Business Model</p>
          <h2 className="font-display text-4xl sm:text-6xl leading-tight">
            Three Revenue Pillars. Eleven Revenue Channels. <span className="text-gradient-ember">One Scalable Platform.</span>
          </h2>
          <p className="mt-6 text-muted-foreground leading-relaxed">
            ClutchCube is designed as a diversified experiential entertainment platform—not a single-attraction venue.
          </p>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            The flagship location validates multiple complementary revenue channels that scale from venue operations to recurring memberships, strategic partnerships, licensing, and franchising.
          </p>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            Together, these create a resilient business model designed for long-term growth and franchise expansion.
          </p>
        </div>

        <div className="space-y-12">
          {categories.map((cat) => (
            <div key={cat.title}>
              <div className="flex items-baseline justify-between mb-2 border-b border-border/60 pb-3">
                <h3 className="font-display text-2xl">{cat.title}</h3>
                <span className="text-[10px] uppercase tracking-[0.35em] text-primary">{cat.label}</span>
              </div>
              <p className="text-sm text-muted-foreground mb-6">{cat.desc}</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {cat.streams.map((s) => {
                  counter += 1;
                  const i = counter;
                  return (
                    <div key={s.k} className="relative p-6 bg-card border border-border/60 rounded-sm hover:border-primary/60 transition group overflow-hidden">
                      <div className="absolute top-0 right-0 font-display text-6xl text-primary/5 group-hover:text-primary/20 transition leading-none px-3 py-1">
                        {String(i).padStart(2, "0")}
                      </div>
                      <h4 className="font-display text-lg text-foreground relative">{s.k}</h4>
                      <p className="mt-2 text-sm text-muted-foreground relative">{s.d}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <p className="mt-12 max-w-3xl text-sm uppercase tracking-[0.25em] text-muted-foreground leading-relaxed">
          The flagship ClutchCube location is more than a destination—it is the proof-of-concept that validates customer demand, operating procedures, unit economics, and the full monetization platform required to support long-term franchise expansion.
        </p>
      </div>
    </section>
  );
}
